/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Devices,
  DeviceType,
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {isValidESN, isValidIPAddress} from '@ion/core/src/utils/DeviceUtils';
import {isValue} from '@ion/core/src/utils/StringUtil';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '@ion/dls/src/components/breadcrumbs/BreadcrumbUtils';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {DeviceFormFields, isVisionDevice} from '@ion/dls/src/editors/DeviceEditorUtils';
import {EditorDialog} from '@ion/dls/src/editors/EditorDialog';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {BulkUploadContainer} from '@ion/dls/src/imports/BulkUploadContainer';
import {BulkUploadResultsPrompt} from '@ion/dls/src/imports/BulkUploadResultsPrompt';
import {dlsColor} from '@ion/dls/src/theme/theme';
import DeviceConfigurationChooser from '@ion/features/src/entities/device/DeviceConfigurationChooser';
import DeviceIdentifierTypeChooser from '@ion/features/src/entities/device/DeviceIdentifierTypeChooser';
import DeviceTypeChooser from '@ion/features/src/entities/device/DeviceTypeChooser';
import {parsePastedIds} from '../../bulk/BulkSelectEntitiesEditorUtils';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {
  BulkAddDevicesFormFields,
  getInitialBulkAddDevicesFormState,
  serialize
} from './BulkAddDevicesEditorUtils';
import {onImportStart} from './ImportDevicesContainerUtils';
import {showBulkAddDevices} from './state';
import {useBulkAddDevicesImport} from './useBulkAddDevicesImport';

const overrides = () => ({
  overrides : {
    marginTop : '16px',
    padding   : '0 16px'
  },
  crumbContainer : {
    textAlign : 'left'
  },
  chevron : {
    fill : dlsColor('grey3')
  },
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  uploadContainer : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center',
    width          : '95%'
  },
  or : {
    fontSize   : '16px',
    fontWeight : 500,
    padding    : 10
  },
  listContainer : {
    minWidth  : 350,
    height    : '100%',
    marginTop : '20px'
  }
});

export interface BulkAddDeviceEditorProps {
  onSwitchToSingleAdd?: (e: MouseEvent<HTMLButtonElement, MouseEvent>, id?: number) => void;
};

/**
 * Display Bulk Add Devices Editor
 */
export const BulkAddDevicesEditor = ({
  onSwitchToSingleAdd
}: BulkAddDeviceEditorProps) => {
  const editorStyles              = useExtendStyles(styles, overrides);
  const classes                   = editorStyles();
  const dispatch                  = useDispatch();
  const {globalGroup}             = useSelector(({globalGroup}) => globalGroup);
  const {account: userAccount}    = useAuthenticatedUser();
  const account                   = globalGroup || userAccount;
  const [formState, setFormState] = useFormState(getInitialBulkAddDevicesFormState());
  const {loading}                 = useSelector(({devices: {bulkAdd}}) => bulkAdd);
  const entityLabelSingle         = translate('common:entityLabels.device');
  const entityLabelPlural         = translate('common:entityLabels.devices');

  const [identifierError, setIdentifierError] = useState(false);
  const [deviceType, setDeviceType]           = useState<DeviceType>();
  const [identifierType, setIdentifierType]   = useState<Options>();

  const deviceIdentifiersRef      = useRef<string[]>([]);

  const {
    templateColumns,
    importParams,
    importStatus,
    importComplete,
    importResults,
    handleImport,
    clearImport
  } = useBulkAddDevicesImport(deviceIdentifiersRef);
  const {
    result,
    valid,
    isImporting,
    show,
    isComplete,
    isError
  } = importResults;

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  useEffect(() => {
    if (deviceIdentifiersRef?.current) {
      handleChooserChange({
        name     : BulkAddDevicesFormFields.DeviceIdentifiers,
        required : true,
        value    : deviceIdentifiersRef.current
      });
    }
  }, [deviceIdentifiersRef?.current]);

  const crumbSingleDevice = {
    label   : translate('common:editors.shared.add', {entityLabel: entityLabelSingle}),
    click   : true,
    onClick : function(e) {
      onSwitchToSingleAdd?.(e);
    }
  };

  const crumbData = defineBreadcrumbs({
    crumbs : [
      crumbSingleDevice,
      {
        label : translate('admin-devices:dialogBulkAdd.title'),
        click : false
      }
    ],
    classes : crumbClasses
  });

  const serializer = function(data: FormState): GenericObject {
    const serialized = serialize(data, account);
    const identifierKey = serialized[BulkAddDevicesFormFields.DeviceIdentifierType];
    return deviceIdentifiersRef.current.map(identifier => ({
      name                : identifier,
      [identifierKey]     : identifier,
      type                : serialized[BulkAddDevicesFormFields.DeviceType],
      deviceConfiguration : serialized[BulkAddDevicesFormFields.DeviceConfiguration]
    }));
  };

  const bulkAddDevices = useCallback(function(devices: Devices) {
    onImportStart({
      items : devices,
      account,
      dispatch
    });
  }, []);

  const checkValidIdentifiers = useCallback((deviceIds: number[], identifierType): boolean => {
    if (deviceIds?.length) {
      if (isEqual(identifierType, DeviceFormFields.IPAddress)) {
        return !(deviceIds.some(ipAddress => !isValidIPAddress(ipAddress)));
      }
      if (isEqual(identifierType, DeviceFormFields.ESN)) {
        return !(deviceIds.some(esn => !isValidESN(esn)));
      }
      return true;
    }
    return false;
  }, [identifierType]);

  const checkValidIdentifierType = useCallback((identifierType: string): boolean => {
    if (isVisionDevice(deviceType) && !isEqual(identifierType, DeviceFormFields.IMEI)) {
      setIdentifierError(true);
      return false;
    }

    return true;
  }, [deviceType]);

  const submitValidation = useMemo(() => ({
    [BulkAddDevicesFormFields.DeviceIdentifierType] : ({value}) => checkValidIdentifierType(value),
    [BulkAddDevicesFormFields.DeviceIdentifiers]    : ({value}) => checkValidIdentifiers(value, identifierType)
  }), [checkValidIdentifiers, deviceType, identifierType]);

  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : bulkAddDevices,
    formState,
    setFormState
  });

  const handleChooserChange = useCallback((
    {name, value, required = false}:
    {name: BulkAddDevicesFormFields, value: any, required: boolean}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  }, []);

  const onCloseDialog = useCallback(function() {
    dispatch(showBulkAddDevices(false));
  }, []);

  const onCancelDialog = useCallback(function() {
    dispatch(showBulkAddDevices(false));
  }, []);

  const onDeviceIdentifierTypeChange = useCallback(function(value: Options) {
    handleChooserChange({
      name     : BulkAddDevicesFormFields.DeviceIdentifierType,
      required : true,
      value
    });
    if (isVisionDevice(deviceType) && !isEqual(value, DeviceFormFields.IMEI)) {
      setIdentifierError(true);
    } else {
      setIdentifierError(false);
    }
    setIdentifierType(value);
  }, [deviceType]);

  const onDeviceTypeChange = useCallback(function(value: DeviceType) {
    handleChooserChange({
      name     : BulkAddDevicesFormFields.DeviceType,
      required : true,
      value
    });
    if (isVisionDevice(value) && !isEqual(identifierType, DeviceFormFields.IMEI)) {
      setIdentifierError(true);
    } else {
      setIdentifierError(false);
    }
    setDeviceType(value);
  }, [identifierType]);

  const onDeviceConfigurationChange = useCallback(function(value: GenericObject) {
    handleChooserChange({
      name     : BulkAddDevicesFormFields.DeviceConfiguration,
      required : true,
      value
    });
  }, []);

  const onDeviceIdentifierListChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    const {value}: {value: string} = e.target;
    const identifiers = parsePastedIds(value);
    const validIdentifiers = [];
    identifiers.forEach(id => {
      if (id.trim()) {
        validIdentifiers.push(String(id));
      }
    });
    deviceIdentifiersRef.current = validIdentifiers;
    handleChooserChange({
      name     : BulkAddDevicesFormFields.DeviceIdentifiers,
      required : true,
      value    : validIdentifiers
    });
  }, []);

  return (
    <>
      {!show && (
        <EditorDialog open
          dialogTitle={translate('admin-devices:dialogBulkAdd.title')}
          onCloseDialog={onCloseDialog}
          onCancelDialog={onCancelDialog}
          onPrimaryButtonClick={handleSubmit}
          primaryButtonLoading={loading}>
          <div className={classes.crumbContainer}>
            <Breadcrumb icon={<ChevronLeft className={classes.chevron} />}
              height={'30px'}
              iconClick={onSwitchToSingleAdd}>
              {crumbData}
            </Breadcrumb>
          </div>
          <form id={'editorBulkAdd'} className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.formWrapper}>
              <FormSection title={' '}
                legend={translate('common:editors.shared.fieldsRequired')}>
                <FormItem fullWidth>
                  <DeviceIdentifierTypeChooser required
                    readOnly={loading}
                    onChange={onDeviceIdentifierTypeChange}
                    hasError={identifierError || formState[BulkAddDevicesFormFields.DeviceIdentifierType]?.error}
                    errorText={identifierError ? translate('admin-devices:dialogBulkAdd.visionError') : ''}
                    defaultValue={formState[BulkAddDevicesFormFields.DeviceIdentifierType]?.value} />
                </FormItem>
                <FormItem fullWidth>
                  <div className={classes.uploadContainer}>
                    <div>
                      <BulkUploadContainer id={'device-importer'}
                        uploadTitle={translate('admin-devices:dialogBulkAdd.uploadTitle')}
                        entity={Entity.device}
                        entityLabel={entityLabelPlural}
                        omitInstructions
                        omitDownloadEntityTemplate
                        importParams={importParams}
                        templateColumns={templateColumns}
                        templateOptions={{}}
                        onDownload={null}
                        handleImport={handleImport}
                        importStatus={importStatus}
                        importComplete={importComplete}
                        clearImport={clearImport} />
                    </div>
                    <div className={classes.or}>
                      {translate('common:conjunctions.or')}
                    </div>
                    <div className={classes.listContainer}>
                      <FormTextField autoComplete='off'
                        multiline
                        minRows={11}
                        maxRows={15}
                        variant='outlined'
                        hasError={formState[BulkAddDevicesFormFields.DeviceIdentifiers]?.error}
                        errorText={translate('admin-devices:view.bulkSciSetting.identifierError')}
                        placeholder={translate('admin-devices:dialogBulkAdd.listPlaceHolder')}
                        onChange={onDeviceIdentifierListChange} />
                    </div>
                  </div>
                </FormItem>
                <FormItem>
                  <DeviceTypeChooser required
                    readOnly={loading}
                    onChange={onDeviceTypeChange}
                    hasError={formState[BulkAddDevicesFormFields.DeviceType]?.error}
                    defaultValue={formState[BulkAddDevicesFormFields.DeviceType]?.value?.title || ''} />
                </FormItem>
                <FormItem>
                  <DeviceConfigurationChooser required
                    readOnly={loading || !formState[BulkAddDevicesFormFields.DeviceType]?.value}
                    isCtcAdmin
                    accountId={entityId(account)}
                    deviceType={formState[BulkAddDevicesFormFields.DeviceType]?.value}
                    onChange={onDeviceConfigurationChange}
                    hasError={formState[BulkAddDevicesFormFields.DeviceConfiguration]?.error}
                    defaultValue={formState[BulkAddDevicesFormFields.DeviceConfiguration]?.value?.title || ''} />
                </FormItem>
              </FormSection>
            </div>
          </form>
        </EditorDialog>
      )}
      <BulkUploadResultsPrompt entity={Entity.device}
        entityLabel={entityLabelPlural}
        result={result}
        valid={valid}
        isImporting={isImporting}
        show={show}
        isComplete={isComplete}
        isError={isError}
        onConfirm={onCloseDialog}
        itemsCount={deviceIdentifiersRef.current.length || 0}
        suppressResultsNotification />
    </>);
};

export default React.memo(BulkAddDevicesEditor);
